import { Component, EventEmitter, OnInit, Output } from "@angular/core";
import { UntilDestroy } from "@ngneat/until-destroy";

import { PercentageBarModel } from "@app/directives/_layout/v-table/_components/cells/percentage-bar/percentage-bar-cell.component";
import { IVTableColumn } from "@app/directives/_layout/v-table/_models/IVTableColumn";
import { IVTableRow } from "@app/directives/_layout/v-table/_models/IVTableRow";
import { VTableService } from "@app/directives/_layout/v-table/_services/v-table.service";
import { SortDirection } from "@app/enum/SortDirection";
import { SortType } from "@app/enum/SortType";
import { IAccount } from "@app/services/account.service";
import { TranslationService } from "@app/services/translation.service";
import { DeviceHealthAccountService } from "../../device-health-account.service";
import { IDeviceHealthAccountSummary } from "../../models/device-health-account-summary";
import './device-health-accounts.component.scss';

@UntilDestroy()
@Component({
    selector: 'device-health-accounts',
    template: require('./device-health-accounts.component.html')
})
export class DeviceHealthAccountsComponent implements OnInit {
    @Output() onAccountSelection = new EventEmitter<IAccount>();

    loading = false;

    tableColumns: IVTableColumn<IDeviceHealthAccountSummary>[] = [];
    tableRows: IVTableRow<IDeviceHealthAccountSummary>[] = [];
    data: IDeviceHealthAccountSummary[] = [];

    constructor(
        private readonly deviceHealthAccountService: DeviceHealthAccountService,
        private readonly vTableService: VTableService,
        private readonly translationService: TranslationService
    ) { }

    async ngOnInit(): Promise<void> {
        this.createTable();
        await this.loadData();
    }

    selectSummary(row: IVTableRow<IDeviceHealthAccountSummary>): void {
        this.onAccountSelection.next(row.data.account);
    }

    private createTable(): void {
        this.tableColumns = [
            {
                header: this.vTableService.createTableHeader({ tKey: "Common.Account", sortable: true }),
                createCellFunction: (summary) => this.vTableService.createTableCell(summary.account.CompanyName, { tooltip: true })
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceHealth.Accounts.TotalErrors", width: 120, sortable: true, sortDirection: SortDirection.Descending, sorted: true, sortType: SortType.Number }),
                createCellFunction: (summary) => this.vTableService.createTableCell(summary.cameraFailingVehiclesCount + summary.telematicFailingVehiclesCount)
            }, 
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceHealth.Accounts.VehiclesReportingTelematicsErrors", sortable: true }),
                createCellFunction: (summary) => this.vTableService.createPercentageBarCell(this.getPercentageTelematicsModel(summary))
            },
            {
                header: this.vTableService.createTableHeader({ tKey: "DeviceHealth.Accounts.VehiclesReportingCameraErrors", sortable: true}),
                createCellFunction: (summary) => this.vTableService.createPercentageBarCell(this.getPercentageCameraModel(summary))
            },       
        ];
    }

    private createTableRows(): void {
        this.tableRows = this.vTableService.createTableRows(this.data, this.tableColumns);
    }

    private async loadData(): Promise<void> {
        this.loading = true;

        this.data = await this.deviceHealthAccountService.getStatsForAccounts();
        this.createTableRows();

        this.loading = false;
    }

    private getPercentageTelematicsModel(summary: IDeviceHealthAccountSummary): PercentageBarModel {
        const mainTextKey = summary.vehiclesCount === 0 
            ? 'DeviceHealth.Accounts.NoVehicles'
            : 'DeviceHealth.Accounts.ErrorsReported';

        return {
            positiveSegment: {
                count: summary.vehiclesCount - summary.telematicFailingVehiclesCount,
                tooltip: this.translationService.t('DeviceHealth.Accounts.VehiclesWithoutErrors')
            },
            negativeSegment: {
                count: summary.telematicFailingVehiclesCount,
                tooltip: this.translationService.t('DeviceHealth.Accounts.VehiclesWithErrors')
            },
            mainText: this.translationService.t(mainTextKey, [summary.telematicFailingVehiclesCount, summary.vehiclesCount])
        }
    }

    private getPercentageCameraModel(summary: IDeviceHealthAccountSummary): PercentageBarModel {
        const mainTextKey = summary.vehiclesCount === 0 
            ? 'DeviceHealth.Accounts.NoVehicles'
            : 'DeviceHealth.Accounts.ErrorsReported';

        return {
            positiveSegment: {
                count: summary.vehiclesCount - summary.cameraFailingVehiclesCount,
                tooltip: this.translationService.t('DeviceHealth.Accounts.VehiclesWithoutErrors')
            },
            negativeSegment: {
                count: summary.cameraFailingVehiclesCount,
                tooltip: this.translationService.t('DeviceHealth.Accounts.VehiclesWithErrors')
            },
            mainText: this.translationService.t(mainTextKey, [summary.cameraFailingVehiclesCount, summary.vehiclesCount])
        }
    }
}